import React, { FC, useRef, useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import { useDivisionSwitch } from 'src/providers/DivisionSwitchProvider';
import { v2Validate, v2IssueToken, V2ValidateResponse, DivisionMembership } from 'src/store/modules/auth/v2Operations';
import { DIVISION_VALIDATED_EVENT } from 'src/hooks/useDivisionCheck';
import {
  getDivisions,
  getCurrentDivisionId,
  saveDivisions,
  saveCurrentDivisionId,
  CURRENT_DIVISION_KEY
} from 'src/utils/divisionStorage';
import { getEnterpriseSsoHost } from 'src/utils/ssoReferrer';
import { useAppSelector } from 'src/utils/Hooks';
import { isV2Enabled } from 'src/utils/authVersion';

const DISMISSED_MISMATCH_KEY = 'sl-division-mismatch-dismissed';
function isDismissedMismatch(divisionId: string): boolean {
  try { return sessionStorage.getItem(DISMISSED_MISMATCH_KEY) === divisionId; } catch { return false; }
}
function clearDismissedMismatch(): void {
  try { sessionStorage.removeItem(DISMISSED_MISMATCH_KEY); } catch { /* ignore */ }
}

const WARNING_ICON = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 6v4m0 2.5h.01M3.072 17h13.856c1.078 0 1.756-1.167 1.218-2.1L11.218 3.1c-.539-.933-1.897-.933-2.436 0L1.854 14.9c-.538.933.14 2.1 1.218 2.1z" stroke="#F35379" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface OmniNavAccountLinkProps {
  avatarClassName: string;
  avatarContent: string;
  containerClassName: string;
  displayName: string;
  displayTextClassName: string;
  isActive: () => boolean;
  linkTo: string;
  onClick?: () => void;
}

const OmniNavAccountLink: FC<OmniNavAccountLinkProps> = ({
  avatarClassName,
  avatarContent,
  containerClassName,
  displayName,
  displayTextClassName,
  isActive,
  linkTo,
  onClick
}) => {
  const isV2 = isV2Enabled();
  const avatarRef = useRef<HTMLDivElement>(null);
  const mismatchAlreadyDetectedRef = useRef(false);

  const { beginSwitch, endSwitch } = useDivisionSwitch();
  const [divisions, setDivisions] = useState<DivisionMembership[] | null>(() => getDivisions());
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(() => getCurrentDivisionId());
  const [divisionMismatch, setDivisionMismatch] = useState(false);
  const [mismatchDivisionName, setMismatchDivisionName] = useState<string | null>(null);
  const [mismatchDivisionId, setMismatchDivisionId] = useState<string | null>(null);
  const [alertAnchor, setAlertAnchor] = useState<HTMLElement | null>(null);

  const session = useAppSelector((state) => state.user?.session);
  const reduxDivisionId = session?.divisionId;
  const currentDivisionId = activeDivisionId || reduxDivisionId;

  const currentDivision = divisions?.find((d) => d.id === currentDivisionId);

  useEffect(() => {
    if (!isV2) return;
    clearDismissedMismatch();
    const fetchDivisions = async () => {
      try {
        const response = await v2Validate();
        if (response.authenticated && response.divisions?.length > 0) {
          saveDivisions(response.divisions);
          setDivisions(response.divisions);
          if (response.division?.id) {
            setActiveDivisionId(response.division.id);
          }
        }
      } catch { /* ignore */ }
    };
    fetchDivisions();
  }, [isV2]);

  useEffect(() => {
    if (divisionMismatch && !mismatchAlreadyDetectedRef.current && avatarRef.current) {
      mismatchAlreadyDetectedRef.current = true;
      setAlertAnchor(avatarRef.current);
    }
  }, [divisionMismatch]);

  useEffect(() => {
    if (!isV2) return;

    const triggerMismatch = (newId: string, newName: string) => {
      setDivisionMismatch(true);
      setMismatchDivisionName(newName);
      setMismatchDivisionId(newId);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== CURRENT_DIVISION_KEY) return;
      const newDivisionId = e.newValue;
      const isDismissed = newDivisionId ? isDismissedMismatch(newDivisionId) : false;
      if (newDivisionId && newDivisionId !== currentDivisionId && !isDismissed) {
        const cached = getDivisions();
        const newDiv = cached?.find((d) => d.id === newDivisionId);
        triggerMismatch(newDivisionId, newDiv?.companyName ? `${newDiv.companyName} ${newDiv.name}` : (newDiv?.name || newDivisionId));
      }
    };

    const handleDivisionValidated = (e: Event) => {
      if (!currentDivisionId) return;
      const response = (e as CustomEvent<V2ValidateResponse>).detail;
      if (!response.authenticated || !response.division) return;
      const isDismissed = isDismissedMismatch(response.division.id);
      if (response.division.id !== currentDivisionId && !isDismissed) {
        triggerMismatch(response.division.id, response.division.companyName ? `${response.division.companyName} ${response.division.name}` : (response.division.name || response.division.id));
        saveDivisions(response.divisions || []);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(DIVISION_VALIDATED_EVENT, handleDivisionValidated);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(DIVISION_VALIDATED_EVENT, handleDivisionValidated);
    };
  }, [isV2, currentDivisionId]);

  const handleDivisionSwitch = useCallback(async (divisionId: string) => {
    try {
      const target = (divisions || getDivisions() || []).find((d) => d.id === divisionId);
      const targetName = target
        ? (target.companyName ? `${target.companyName} ${target.name}` : (target.name || divisionId))
        : divisionId;
      beginSwitch(targetName);
      const response = await v2Validate();
      if (!response.authenticated || !response.sessionToken) {
        window.location.href = '/sign_out';
        return;
      }
      const latestDivisions = response.divisions || divisions || getDivisions() || [];
      if (response.divisions?.length > 0) {
        saveDivisions(response.divisions);
        setDivisions(response.divisions);
      }
      await v2IssueToken(response.sessionToken, divisionId, 'omni');
      const switchedDivision = latestDivisions.find((division) => division.id === divisionId);
      const accessLevel = switchedDivision?.appAccess?.omni;
      const hasOmniAccess = Boolean(accessLevel) && accessLevel !== 'blocked';
      saveCurrentDivisionId(divisionId);
      setActiveDivisionId(divisionId);
      if (!hasOmniAccess) {
        const enterpriseSsoHost = getEnterpriseSsoHost();
        const divisionName = switchedDivision
          ? (switchedDivision.companyName ? `${switchedDivision.companyName} ${switchedDivision.name}` : switchedDivision.name)
          : divisionId;
        endSwitch();
        window.location.href = `https://${enterpriseSsoHost}/no-access?app=omni&divName=${encodeURIComponent(divisionName)}`;
        return;
      }
      setDivisionMismatch(false);
      setMismatchDivisionName(null);
      setMismatchDivisionId(null);
      mismatchAlreadyDetectedRef.current = false;
      clearDismissedMismatch();
      window.dispatchEvent(
        new CustomEvent('sl:division-context-changed', {
          detail: { divisionId }
        })
      );
      endSwitch();
    } catch (error) {
      console.error('[OmniNavAccountLink] Division switch failed:', error);
      endSwitch();
    }
  }, [divisions, beginSwitch, endSwitch]);

  const handleStay = async () => {
    setAlertAnchor(null);
    if (currentDivisionId) {
      await handleDivisionSwitch(currentDivisionId);
    }
  };

  const handleReloadWithNewDivision = () => {
    setAlertAnchor(null);
    if (mismatchDivisionId) {
      handleDivisionSwitch(mismatchDivisionId);
    }
  };

  return (
    <div className={containerClassName}>
      <NavLink
        activeClassName="navigation__item-link--active"
        className="navigation__item-link navigation__item-link--avatar"
        isActive={isActive}
        to={linkTo}
        onClick={(e) => {
          if (divisionMismatch) {
            e.preventDefault();
            setAlertAnchor(avatarRef.current);
            return;
          }
          if (onClick) onClick();
        }}
      >
        <div ref={avatarRef} style={{ position: 'relative' }}>
          <Tooltip title={divisionMismatch ? '' : displayName} placement="right">
            <Avatar
              className={avatarClassName}
              style={divisionMismatch ? { boxShadow: '0 0 0 3px #F35379' } : undefined}
            >
              {avatarContent}
            </Avatar>
          </Tooltip>
          {divisionMismatch && (
            <div style={{
              position: 'absolute', top: -1, right: -1,
              width: 10, height: 10, borderRadius: '50%',
              background: '#F35379', border: '2px solid #1B2B4B'
            }} />
          )}
        </div>
        <div className={displayTextClassName}>{displayName}</div>
      </NavLink>

      {divisionMismatch && (
        <Popover
          open={Boolean(alertAnchor)}
          anchorEl={alertAnchor}
          onClose={() => setAlertAnchor(null)}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              width: 320, borderRadius: '8px', border: '1px solid #dedede',
              boxShadow: '0 0 16px 0 rgba(0,0,0,0.06)', padding: '24px',
              marginLeft: '12px', overflow: 'visible'
            }
          }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: '#FFEEED',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12
          }}>
            {WARNING_ICON}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, lineHeight: '24px', marginBottom: 8 }}>Division Changed</div>
          <div style={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.08px', marginBottom: 20 }}>
            You switched to <strong>{mismatchDivisionName}</strong> in another app. This page was loaded with{' '}
            <strong>{currentDivision?.companyName ? `${currentDivision.companyName} ${currentDivision.name}` : (currentDivision?.name || currentDivisionId)}</strong>.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleStay} style={{
              flex: 1, height: 32, background: '#fff', color: '#031425',
              border: '1px solid #E8E8ED', borderRadius: 4, fontSize: 14,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'Roboto, sans-serif'
            }}>Stay</button>
            <button onClick={handleReloadWithNewDivision} style={{
              flex: 1, height: 32, background: '#031425', color: '#fff',
              border: '1px solid #031425', borderRadius: 4, fontSize: 14,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'Roboto, sans-serif'
            }}>Switch Division</button>
          </div>
        </Popover>
      )}
    </div>
  );
};

export default OmniNavAccountLink;
